import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  InteractiveWebhookNodeData,
  InteractiveWebhookOption,
} from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for interactive webhook node functionality.
 * Handles URL, options, and allowExtensionAsOption updates.
 */
export const useInteractiveWebhookNode = (
  id: string,
  data: InteractiveWebhookNodeData,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Normalize options - use options if available, otherwise children (legacy)
  // Filter out empty/invalid options - don't show default options
  const rawOptions: InteractiveWebhookOption[] =
    data.options || data.children || []

  // Normalize options - handle legacy format with option_id, id, etc.
  const normalizedOptions: InteractiveWebhookOption[] = rawOptions
    .map((option: unknown) => {
      if (!option || typeof option !== 'object') return null
      const opt = option as Record<string, unknown>

      // Check for various field names that might contain the option value
      const value =
        opt.value ||
        opt.option ||
        opt.option_id ||
        opt.id ||
        (typeof opt === 'string' ? opt : null)
      const label = opt.label

      // If we have a value (even if it's an id), create an option
      if (value) {
        return {
          value: String(value),
          label: label ? String(label) : undefined,
        } as InteractiveWebhookOption
      }

      // If it's an empty object, skip it
      if (Object.keys(opt).length === 0) {
        return null
      }

      // If it has other fields but no value, try to use the first non-id field
      const nonIdFields = Object.keys(opt).filter(
        (key) => key !== 'id' && key !== 'target',
      )
      if (nonIdFields.length > 0) {
        const firstField = nonIdFields[0]
        return {
          value: String(opt[firstField] || ''),
          label: label ? String(label) : undefined,
        } as InteractiveWebhookOption
      }

      return null
    })
    .filter((opt): opt is InteractiveWebhookOption => opt !== null)

  const initialOptions: InteractiveWebhookOption[] = normalizedOptions.filter(
    (option) => {
      // Only include options that have a meaningful value (not just default/empty)
      // Filter out empty strings, whitespace-only values, and null/undefined
      if (!option) return false
      const hasValue = option.value && option.value.trim() !== ''
      const hasLabel = option.label && option.label.trim() !== ''
      const shouldInclude = hasValue || hasLabel

      return shouldInclude
    },
  )

  const [url, setUrl] = useState(data.url || '')
  const [allowExtensionAsOption, setAllowExtensionAsOption] = useState(
    data.allowExtensionAsOption || false,
  )
  const [options, setOptions] =
    useState<InteractiveWebhookOption[]>(initialOptions)

  // Local state for option inputs (to prevent focus loss)
  const [optionInputs, setOptionInputs] = useState<Record<number, string>>(
    () => {
      const inputs: Record<number, string> = {}
      initialOptions.forEach((option, index) => {
        inputs[index] = option.value || ''
      })
      return inputs
    },
  )

  const isUpdatingRef = useRef(false)

  // Sync local state when node data changes from outside (not from our own updates)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const newUrl = data.url || ''
      setUrl((prevUrl) => {
        if (newUrl !== prevUrl) {
          return newUrl
        }
        return prevUrl
      })
      const newAllowExtension = data.allowExtensionAsOption || false
      setAllowExtensionAsOption((prev) =>
        newAllowExtension !== prev ? newAllowExtension : prev,
      )
      const rawNewOptions = data.options || data.children || []
      // Filter out empty/invalid options - don't show default options
      const newOptions = rawNewOptions.filter((option) => {
        // Only include options that have a meaningful value (not just default/empty)
        // Filter out empty strings, whitespace-only values, and null/undefined
        if (!option) return false
        const hasValue = option.value && option.value.trim() !== ''
        const hasLabel = option.label && option.label.trim() !== ''
        return hasValue || hasLabel
      })
      setOptions((prevOptions) => {
        // Only update if options actually changed
        if (JSON.stringify(newOptions) !== JSON.stringify(prevOptions)) {
          // Also sync option inputs
          const inputs: Record<number, string> = {}
          newOptions.forEach((option, index) => {
            inputs[index] = option.value || ''
          })
          setOptionInputs(inputs)
          return newOptions
        }
        return prevOptions
      })
    }
  }, [data.url, data.allowExtensionAsOption, data.options, data.children])

  const handleUrlChange = useCallback(
    (newUrl: string) => {
      // Only update local state - don't update React Flow yet
      isUpdatingRef.current = true
      setUrl(newUrl)
    },
    [url],
  )

  const handleUrlBlur = useCallback(() => {
    // Update React Flow when user is done typing (on blur)
    updateNodeData(id, {
      ...dataRef.current,
      url: url || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, url])

  const handleAllowExtensionChange = useCallback(
    (newAllowExtensionAsOption: boolean) => {
      setAllowExtensionAsOption(newAllowExtensionAsOption)
      isUpdatingRef.current = true
      updateNodeData(id, {
        ...dataRef.current,
        allowExtensionAsOption: newAllowExtensionAsOption,
      })
    },
    [id, updateNodeData],
  )

  const handleAddOption = useCallback(() => {
    // Find the highest existing value and increment by 1 (matches legacy behavior)
    let maxValue = 0
    options.forEach((option) => {
      const valueNum = parseInt(option.value || '0', 10)
      if (!isNaN(valueNum) && valueNum > maxValue) {
        maxValue = valueNum
      }
    })
    const nextValue = String(maxValue + 1)

    const newOption: InteractiveWebhookOption = {
      value: nextValue,
      label: '',
    }
    const newOptions = [...options, newOption]
    setOptions(newOptions)
    // Add to local input state
    setOptionInputs((prev) => ({
      ...prev,
      [options.length]: nextValue,
    }))
    isUpdatingRef.current = true
    updateNodeData(id, {
      ...dataRef.current,
      options: newOptions,
      // Clear legacy children field
      children: undefined,
    })
  }, [id, options, updateNodeData])

  const handleRemoveOption = useCallback(
    (index: number) => {
      const newOptions = options.filter((_, i) => i !== index)
      setOptions(newOptions)
      // Remove from local input state and reindex
      setOptionInputs((prev) => {
        const newInputs: Record<number, string> = {}
        newOptions.forEach((_, i) => {
          if (i < index) {
            newInputs[i] = prev[i] || ''
          } else {
            newInputs[i] = prev[i + 1] || ''
          }
        })
        return newInputs
      })
      isUpdatingRef.current = true
      updateNodeData(id, {
        ...dataRef.current,
        options: newOptions.length > 0 ? newOptions : undefined,
        // Clear legacy children field
        children: undefined,
      })
    },
    [id, options, updateNodeData],
  )

  const handleOptionChange = useCallback((index: number, value: string) => {
    // Only update local input state - don't update React Flow yet
    isUpdatingRef.current = true
    setOptionInputs((prev) => ({
      ...prev,
      [index]: value,
    }))
  }, [])

  const handleOptionBlur = useCallback(
    (index: number) => {
      // Update React Flow when user is done typing (on blur)
      const optionValue = optionInputs[index] || ''
      // Parse and clamp option value to valid range: 0-9999
      const numValue = optionValue ? parseInt(optionValue, 10) : undefined
      const clampedValue =
        numValue !== undefined && !isNaN(numValue)
          ? Math.max(0, Math.min(9999, numValue))
          : undefined
      // Convert clamped value to string (matching legacy behavior)
      const clampedOptionValue =
        clampedValue !== undefined ? String(clampedValue) : optionValue
      const newOptions = [...options]
      newOptions[index] = {
        ...newOptions[index],
        value: clampedOptionValue,
      }
      setOptions(newOptions)
      updateNodeData(id, {
        ...dataRef.current,
        options: newOptions,
        // Clear legacy children field
        children: undefined,
      })
      isUpdatingRef.current = false
    },
    [id, options, optionInputs, updateNodeData],
  )

  return {
    currentUrl: url,
    currentAllowExtensionAsOption: allowExtensionAsOption,
    currentOptions: options,
    optionInputs,
    handleUrlChange,
    handleUrlBlur,
    handleAllowExtensionChange,
    handleAddOption,
    handleRemoveOption,
    handleOptionChange,
    handleOptionBlur,
    hasData: !!(url || options.length > 0),
  }
}
